import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Banks } from '../../../models/banks';
import Title from '../../../components/Title';
import { NavLink } from 'react-router-dom';
import useAxiosInterceptor from '../../AxiosInterceptor';
import { useTheme, useMediaQuery, Button, Box, Card, CardContent, Tooltip } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
  stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import { useDrawer } from '../../../components/context/DrawerContext';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
  iconActionColumnSizing,
  simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import { SimpleColumn, SimpleRow, RowId } from '../../../components/table/interface';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const Bancos: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = "Bancos - Mis Rutas";
  useAxiosInterceptor();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { drawerOpen } = useDrawer();

  const [banks, setBanks] = useState<Banks[]>([]);
  const { canCreate, canUpdate, canDelete } = useResourcePermissions('/administrar/bancos');

  useEffect(() => {
    const fetchBanks = async () => {
      showLoading();
      try {
        const { data } = await axios.get('banks');
        setBanks(data.data);
        await new Promise((resolve) => setTimeout(resolve, 500));
        showAlert('Se ha realizado la consulta exitosamente!', 'info');
      } catch (error) {
        showAlert('Error al obtener los bancos.', 'error');
      } finally {
        hideLoading();
      }
    };
    fetchBanks();
  }, []);

  const handleDeleteBank = async (id: number) => {
    if (!canDelete) return;

    const confirmDelete = window.confirm("¿Estás seguro de que deseas eliminar este banco?");
    if (!confirmDelete) return;

    try {
      showLoading();
      await axios.delete(`banks/${id}`);
      setBanks(prev => prev.filter(b => b.id !== id));
      showAlert("Banco eliminado correctamente", "success");
    } catch (error) {
      console.error(error);
      showAlert("Error al eliminar el banco", "error");
    } finally {
      hideLoading();
    }
  };

  const simpleColumns: SimpleColumn[] = [
    { field: 'name', headerName: 'Banco', flex: 1, minWidth: 200, wrapText: true },
    { field: 'country', headerName: 'País', flex: 1, minWidth: 180, wrapText: true },
    {
      field: 'opt_edit',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => (
        <PermissionButton
          allowed={canUpdate}
          deniedTooltip="Sin permiso para editar"
          tooltip="Editar"
          variant="contained"
          color="primary"
          sx={{ ...simpleTableIconButtonSx, ml: 1 }}
          component={NavLink}
          to={`/administrar/bancos/${row.id}/editar`}
        >
            <EditIcon fontSize="small" />
        </PermissionButton>
      ),
    },
    {
      field: 'opt_delete',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const handleClick = () => {
          if (Number.isFinite(numericId)) {
            handleDeleteBank(numericId);
          }
        };
        return (
          <PermissionButton
            allowed={canDelete}
            deniedTooltip="Sin permiso para eliminar"
            tooltip="Eliminar"
            variant="contained"
            color="error"
            sx={{ ...simpleTableIconButtonSx, ml: 1 }}
            onClick={handleClick}
          >
              <DeleteIcon fontSize="small" />
          </PermissionButton>
        );
      },
    },
  ];

  const rows: SimpleRow[] = banks.map((bank) => ({
    id: bank.id as RowId,
    name: bank.name,
    country: bank.type_country?.name || 'Sin país',
  }));

  return (
    <HeaderResponsive>
      <StickyListToolbar>
        <Title>Información de Bancos</Title>
        <Box sx={{ ml: 'auto' }}>
          <PermissionButton
            allowed={canCreate}
            deniedTooltip="Sin permiso para crear"
            size="small"
            tooltip="Agregar"
            variant="contained"
            startIcon={isMobile ? null : <AddIcon />}
            component={NavLink}
            to="/administrar/bancos/add"
          >
            {isMobile ? <AddIcon /> : 'Agregar'}
          </PermissionButton>
        </Box>
      </StickyListToolbar>

      <Card>
        <CardContent
          sx={{
            width: (theme) => stickyListRegionWidth(theme, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
          }}
        >
          {banks.length > 0 ? (
            <SimpleCustomTable
              rows={rows}
              columns={simpleColumns}
              maxHeight={600}
              rowHeight={24}
              showBorders
              showCount
              exportCsv
              exportFileName="bancos.csv"
              wrapCellContent
              columnGroups={[{ header: 'Opciones', fields: ['opt_edit', 'opt_delete'] }]}
            />
          ) : (
            <EmptyStateMessage message="No existen registros de bancos" align="left" />
          )}
        </CardContent>
      </Card>
    </HeaderResponsive>
  );
};

export default Bancos;
